import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar as CalendarIcon, CheckCircle2, Loader2, MessageCircle } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { buildWhatsAppUrl } from "@/lib/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROPERTY_TYPES = ["1 BHK", "2 BHK", "3 BHK", "4+ BHK", "Villa", "Office", "Shop", "Warehouse"];

const initialState = {
  name: "", mobile: "", email: "",
  pickup_location: "", drop_location: "",
  shifting_date: null,
  property_type: "",
  floor_number: "",
  lift_available: "",
  packing_required: "",
  notes: "",
};

export default function QuoteForm({ compact = false }) {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const validate = () => {
    if (!form.name || form.name.trim().length < 2) return "Please enter your name";
    if (!/^[0-9+\-\s()]{7,20}$/.test(form.mobile)) return "Please enter a valid mobile number";
    if (!form.pickup_location) return "Pickup location is required";
    if (!form.drop_location) return "Drop location is required";
    if (!form.shifting_date) return "Please select a shifting date";
    if (!form.property_type) return "Please select a property type";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        shifting_date: form.shifting_date ? format(form.shifting_date, "yyyy-MM-dd") : "",
      };
      await axios.post(`${API}/quotations`, payload);
      setSuccess(true);
      toast.success("Quote request submitted. We'll contact you shortly.");
    } catch (e) {
      console.error(e);
      toast.error("Could not submit. Please try WhatsApp or call us.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    const waMsg = `Hello, I just submitted a quotation request. Name: ${form.name}, From: ${form.pickup_location}, To: ${form.drop_location}, Date: ${form.shifting_date ? format(form.shifting_date, "PPP") : ""}, Property: ${form.property_type}`;
    return (
      <div data-testid="quote-success" className="rounded-2xl border border-green-200 bg-green-50 p-8 sm:p-10 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-green-500 text-white grid place-items-center mb-5">
          <CheckCircle2 className="w-9 h-9" />
        </div>
        <h3 className="font-[Outfit] text-2xl sm:text-3xl font-bold text-[#0F172A]">Thank you!</h3>
        <p className="mt-3 text-slate-600 max-w-md mx-auto">Our team will contact you shortly with a transparent, itemized quotation.</p>
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href={buildWhatsAppUrl(waMsg)}
            target="_blank"
            rel="noreferrer"
            data-testid="quote-whatsapp-followup"
            className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white rounded-full px-6 py-3 font-semibold transition"
          >
            <MessageCircle className="w-4 h-4" /> Continue on WhatsApp
          </a>
          <button
            onClick={() => { setForm(initialState); setSuccess(false); }}
            className="text-sm font-semibold text-[#081537] hover:text-[#081537]"
          >
            Submit another request
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="quote-form"
      className={cn(
        "bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-xl shadow-blue-900/5",
        compact ? "" : "lg:p-10"
      )}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Full Name *">
          <Input data-testid="quote-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="John Doe" />
        </Field>
        <Field label="Mobile Number *">
          <Input data-testid="quote-mobile" value={form.mobile} onChange={(e) => update("mobile", e.target.value)} placeholder="+91 98765 43210" inputMode="tel" />
        </Field>
        <Field label="Email">
          <Input data-testid="quote-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@example.com" />
        </Field>
        <Field label="Shifting Date *">
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="quote-date-trigger"
                className="w-full flex items-center justify-between border border-slate-300 rounded-lg px-4 h-11 text-left bg-white text-slate-700 hover:border-slate-400"
              >
                {form.shifting_date ? format(form.shifting_date, "PPP") : <span className="text-slate-400">Pick a date</span>}
                <CalendarIcon className="w-4 h-4 text-slate-400" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-auto" align="start">
              <Calendar
                mode="single"
                selected={form.shifting_date}
                onSelect={(d) => update("shifting_date", d)}
                disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </Field>
        <Field label="Pickup Location *">
          <Input data-testid="quote-pickup" value={form.pickup_location} onChange={(e) => update("pickup_location", e.target.value)} placeholder="Edappally, Kochi" />
        </Field>
        <Field label="Drop Location *">
          <Input data-testid="quote-drop" value={form.drop_location} onChange={(e) => update("drop_location", e.target.value)} placeholder="Kakkanad, Kochi" />
        </Field>
        <Field label="Property Type *">
          <Select value={form.property_type} onValueChange={(v) => update("property_type", v)}>
            <SelectTrigger data-testid="quote-property-type"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {PROPERTY_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Floor Number">
          <Input data-testid="quote-floor" value={form.floor_number} onChange={(e) => update("floor_number", e.target.value)} placeholder="e.g. 3rd floor" />
        </Field>
        <Field label="Lift Available">
          <Select value={form.lift_available} onValueChange={(v) => update("lift_available", v)}>
            <SelectTrigger data-testid="quote-lift"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Yes">Yes</SelectItem>
              <SelectItem value="No">No</SelectItem>
              <SelectItem value="N/A">N/A</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Packing Required">
          <Select value={form.packing_required} onValueChange={(v) => update("packing_required", v)}>
            <SelectTrigger data-testid="quote-packing"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Full Packing">Full Packing</SelectItem>
              <SelectItem value="Partial Packing">Partial Packing</SelectItem>
              <SelectItem value="No Packing">No Packing</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <div className="sm:col-span-2">
          <Field label="Additional Notes">
            <Textarea data-testid="quote-notes" rows={4} value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Tell us about fragile items, large appliances, parking access, etc." />
          </Field>
        </div>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <Button
          type="submit"
          disabled={loading}
          data-testid="quote-submit-btn"
          className="bg-[#081537] hover:bg-[#0F2152] text-white rounded-full px-8 h-12 font-semibold shadow-md shadow-blue-900/20 sm:flex-1"
        >
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : "Request a Private Consultation"}
        </Button>
        <a
          href={buildWhatsAppUrl()}
          target="_blank"
          rel="noreferrer"
          data-testid="quote-form-whatsapp-btn"
          className="inline-flex items-center justify-center gap-2 border-2 border-slate-200 hover:border-green-500 text-slate-700 hover:text-green-600 rounded-full px-6 h-12 font-semibold transition"
        >
          <MessageCircle className="w-4 h-4" /> Or chat on WhatsApp
        </a>
      </div>
      <p className="mt-4 text-xs text-slate-500">By submitting, you agree to be contacted regarding your shifting request. We respect your privacy.</p>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</Label>
      {children}
    </div>
  );
}
